<template>
  <div ref="cardEl" class="ofc" :class="{ 'ofc--compact': compact }">
    <div v-if="offer.media?.[0]" class="ofc-media">
      <img :src="offer.media[0]" :alt="offer.title" />
    </div>
    <div
      v-else
      class="ofc-media ofc-media--placeholder"
      :style="{ background: kind.color }"
    >
      <f7-icon :f7="kind.icon" size="26" color="white" />
    </div>

    <div class="ofc-body">
      <div class="ofc-top">
        <span class="ofc-kind">{{ $t(kindLabelKey) }}</span>
        <span v-if="offer.category" class="ofc-cat">{{ offer.category }}</span>
      </div>

      <div class="ofc-title">{{ offer.title }}</div>
      <div v-if="offer.description && !compact" class="ofc-desc">
        {{ offer.description }}
      </div>
      <div class="ofc-price-row">
        <span
          v-if="offer.price != null && offer.price !== ''"
          class="ofc-price"
        >
          {{ fmtCfa(offer.price,offer.currency) }}
        </span>
        <span v-else class="ofc-price ofc-price--contact">{{
          $t("marketplace.priceOnRequest")
        }}</span>
        <span v-if="offer.negotiable" class="ofc-negotiable">{{
          $t("marketplace.negotiable")
        }}</span>
      </div>

      <div class="ofc-seller">
        <span class="ofc-seller-name">{{ offer.sellerName }}</span>
        <span v-if="offer.sellerVerified" class="ofc-verified">
          <f7-icon f7="checkmark_shield_fill" size="11" />
          {{ $t("marketplace.sellerBadge") }}
        </span>
        <span v-if="offer.contactMethod" class="ofc-contact">
          <f7-icon :f7="contactIcon" size="11" />
        </span>
      </div>

      <div class="ofc-meta">
        <span>{{ dateLabel }}</span>
        <span
          v-if="showStatus && statusBadge"
          class="ofc-status"
          :class="`ofc-status--${offer.status}`"
        >
          {{ statusBadge }}
        </span>
      </div>

      <div
        v-if="offer.status === 'CHANGES_REQUESTED' && offer.changeNote"
        class="ofc-note"
      >
        {{ $t("marketplace.changesRequestedNote", { note: offer.changeNote }) }}
      </div>
      <div
        v-if="offer.status === 'REJECTED' && offer.rejectionNote"
        class="ofc-note ofc-note--reject"
      >
        {{ $t("marketplace.rejectionNote", { note: offer.rejectionNote }) }}
      </div>

      <div v-if="hasActions" class="ofc-actions">
        <slot name="actions" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, useSlots } from "vue";
import { offerKindMeta } from "../../js/pinia-stores/marketplace.js";
import { fmtCfa, CONTACT_METHODS } from "../../constants/listing-options.js";
import i18n from "../../js/i18n.js";
import { useEngagementTracker } from "../../js/composables/useEngagementTracker";

const props = defineProps({
  offer: { type: Object, required: true },
  compact: { type: Boolean, default: false },
  showStatus: { type: Boolean, default: false },
  placementType: { type: String, default: "MARKETPLACE" },
  placementId: { type: String, default: null },
});

const slots = useSlots();

const emit = defineEmits(["feature", "unfeature"]);

const cardEl = ref(null);
const tracker = useEngagementTracker();
let stopObserve;

function base() {
  return {
    contentType: "LISTING",
    contentId: props.offer.id,
    promotionId: props.offer.promotionId || null,
    placementType: props.placementType,
    placementId: props.placementId,
  };
}

onMounted(() => {
  stopObserve = tracker.observeViewable(cardEl.value, base());
});
onBeforeUnmount(() => stopObserve?.());

const { t } = i18n.global;

const kind = computed(() => offerKindMeta(props.offer.kind));

const contactIcon = computed(() => {
  const meta = CONTACT_METHODS.find(
    (c) => c.code === props.offer.contactMethod,
  );
  return meta?.icon || "message_fill";
});

const kindLabelKey = computed(() => {
  const map = {
    PERSONAL_OFFER: "marketplace.kindPersonal",
    BUSINESS_SHOP: "marketplace.kindShop",
    PRODUCT_LISTING: "marketplace.kindProduct",
    SERVICE_LISTING: "marketplace.kindService",
  };
  return map[props.offer.kind] || "marketplace.kindPersonal";
});

const dateLabel = computed(() => {
  const isSubmitted =
    props.offer.status === "PENDING" ||
    props.offer.status === "CHANGES_REQUESTED" ||
    props.offer.status === "REJECTED";
  const source = isSubmitted
    ? props.offer.createdAt
    : props.offer.publishedAt || props.offer.createdAt;
  const fmt = new Date(source).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
  });
  return isSubmitted
    ? t("marketplace.submittedDate", { date: fmt })
    : t("marketplace.publishedDate", { date: fmt });
});

const statusBadge = computed(() => {
  const map = {
    PUBLISHED: t("marketplace.tabsPublished"),
    PENDING: t("marketplace.tabsPending"),
    CHANGES_REQUESTED: t("marketplace.statusChangesRequested"),
    REMOVED: t("marketplace.tabsRemoved"),
    REJECTED: t("marketplace.tabsRemoved"),
  };
  return map[props.offer.status] || "";
});

const hasActions = computed(() => Object.keys(slots).length > 0);
</script>

<style scoped>
.ofc {
  display: flex;
  gap: 12px;
  background: var(--f7-page-bg-color, #fff);
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 10px;
  border: 1px solid var(--f7-list-item-border-color, var(--f7-list-item-border-color, #e0e0e0));
}
.ofc-media {
  flex-shrink: 0;
  width: 92px;
  height: 92px;
  border-radius: 10px;
  overflow: hidden;
  background: var(--f7-list-bg-color, #f5f5f5);
}
.ofc-media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.ofc-media--placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}
.ofc-body {
  flex: 1;
  min-width: 0;
}
.ofc-top {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 4px;
}
.ofc-kind {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--f7-theme-color);
}
.ofc-cat {
  font-size: 10px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.ofc-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--f7-text-color, #333);
  line-height: 1.35;
}
.ofc-desc {
  font-size: 12px;
  color: var(--f7-on-surface-variant, #8e8e93);
  margin-top: 3px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.ofc-price-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 4px;
}
.ofc-price {
  font-size: 14px;
  font-weight: 700;
  color: var(--avocash-green, #1a6b42);
}
.ofc-price--contact {
  font-size: 12px;
  font-weight: 500;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.ofc-negotiable {
  font-size: 10px;
  color: #b25e09;
  background: color-mix(in srgb, #b25e09 10%, transparent);
  border-radius: 8px;
  padding: 1px 7px;
}
.ofc-seller {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  font-size: 11px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.ofc-verified {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: var(--avocash-green, #1a6b42);
  font-weight: 600;
}
.ofc-contact {
  display: inline-flex;
  align-items: center;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.ofc-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 6px;
  font-size: 11px;
  color: var(--f7-on-surface-variant, #8e8e93);
}
.ofc-status {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 10px;
}
.ofc-status--PUBLISHED {
  background: color-mix(
    in srgb,
    var(--avocash-green, #1a6b42) 12%,
    transparent
  );
  color: var(--avocash-green, #1a6b42);
}
.ofc-status--PENDING {
  background: color-mix(in srgb, #b25e09 12%, transparent);
  color: #b25e09;
}
.ofc-status--CHANGES_REQUESTED,
.ofc-status--REJECTED,
.ofc-status--REMOVED {
  background: color-mix(in srgb, #c0392b 12%, transparent);
  color: #c0392b;
}
.ofc-note {
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.45;
  color: #b25e09;
  background: color-mix(in srgb, #b25e09 8%, transparent);
  border-radius: 8px;
  padding: 6px 8px;
}
.ofc-note--reject {
  color: #c0392b;
  background: color-mix(in srgb, #c0392b 8%, transparent);
}
.ofc-actions {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 10px;
}
.ofc-actions :deep(.f7-button),
.ofc-actions :deep(button) {
  flex: 1 1 auto;
  min-width: 90px;
  margin: 0;
}
</style>
